import React, { forwardRef, useId } from "react";
import PropTypes from "prop-types";
import Icon from "@mdi/react";
import { mdiCheckBold } from "@mdi/js";
import "./CustomFormInput.css";

/**
 * Available color variants for CustomFormInput
 * @constant
 */
export const INPUT_VARIANTS = ["turquoise", "purple", "purple-light", "orange", "green"];

/**
 * Available layout options for CustomFormInput
 * @constant
 */
export const INPUT_STYLES = ["standard", "pill"];

/**
 * CustomFormInput Component
 *
 * A unified, reusable form input component for checkboxes and radio buttons
 * that matches Dance Localé's brand styling with full React Hook Form integration.
 *
 * @component
 * @example
 * // Standard checkbox
 * <CustomFormInput
 *   type="checkbox"
 *   label="Accept terms"
 *   checked={value}
 *   onChange={handler}
 * />
 *
 * @example
 * // With React Hook Form
 * const { register } = useForm();
 * <CustomFormInput
 *   type="checkbox"
 *   label="Accept terms"
 *   {...register('terms')}
 * />
 *
 * @example
 * // Pill-style radio button
 * <CustomFormInput
 *   type="radio"
 *   layout="pill"
 *   variant="turquoise"
 *   label="30 min"
 *   name="duration"
 *   value="30"
 * />
 */
const CustomFormInput = forwardRef(
    (
        {
            type = "checkbox",
            variant = "turquoise",
            layout = "standard",
            label,
            className = "",
            disabled = false,
            error = null,
            hint = null,
            id,
            name,
            value,
            checked,
            onChange,
            ...rest
        },
        ref
    ) => {
        // Generate unique ID using React's useId hook for guaranteed uniqueness
        // Sanitize useId() output (removes colons and special chars like :r1: → r1)
        const reactId = useId();
        const safeReactId = reactId.replace(/[^a-zA-Z0-9_-]/g, "");
        const inputId = id ?? `custom-input-${safeReactId}`;

        // Normalize variant defensively (convert to lowercase for case-insensitive matching)
        const normalizedVariant = String(variant).toLowerCase();

        // Development warnings for missing props
        if (process.env.NODE_ENV !== "production") {
            // Warning: missing name can cause form integration issues
            if (!name && type === "radio") {
                console.warn(
                    '[CustomFormInput] Radio buttons require a "name" prop to function correctly. ' +
                        "All radio buttons in a group must share the same name."
                );
            }
        }

        // Determine wrapper class based on layout
        const wrapperClass =
            layout === "pill"
                ? `custom-input-pill custom-input-pill-${normalizedVariant}`
                : `custom-input-wrapper custom-input-${type}`;

        // Combine all classes
        const finalWrapperClass = `${wrapperClass} ${error ? "has-error" : ""} ${disabled ? "is-disabled" : ""} ${className}`;

        // For standard layout, add variant class to input
        const inputClass =
            layout === "standard"
                ? `custom-input-native custom-input-variant-${normalizedVariant}`
                : "custom-input-native-hidden";

        // Build aria-describedby to support both hint and error
        const ariaDescribedBy =
            [error && `${inputId}-error`, hint && `${inputId}-hint`].filter(Boolean).join(" ") || undefined;

        // React Hook Form compatibility: only pass checked when explicitly provided
        // This allows uncontrolled mode to work properly
        const inputProps = {
            ref,
            type,
            id: inputId,
            name,
            value,
            onChange,
            disabled,
            className: inputClass,
            "aria-invalid": error ? "true" : "false",
            "aria-describedby": ariaDescribedBy,
            ...rest,
        };

        // Only add checked prop if it's explicitly provided (allows uncontrolled mode)
        if (checked !== undefined) {
            inputProps.checked = checked;
        }

        /**
         * Standard Layout Rendering
         * Traditional checkbox/radio with custom-styled native input
         * Note: htmlFor removed since input is nested inside label (implicit association)
         */
        if (layout === "standard") {
            return (
                <div className={finalWrapperClass}>
                    <label className="custom-input-label">
                        <input {...inputProps} />
                        {/* MDI checkmark icon for checkbox - positioned absolutely */}
                        {type === "checkbox" && (
                            <Icon path={mdiCheckBold} size={0.6} className="custom-input-checkmark-icon" />
                        )}
                        {label && <span className="custom-input-label-text">{label}</span>}
                    </label>

                    {/* Hint text */}
                    {hint && !error && (
                        <p id={`${inputId}-hint`} className="custom-input-hint">
                            {hint}
                        </p>
                    )}

                    {/* Error message */}
                    {error && (
                        <p id={`${inputId}-error`} className="custom-input-error">
                            {error}
                        </p>
                    )}
                </div>
            );
        }

        /**
         * Pill Layout Rendering
         * Button-like appearance with hidden native input
         * Similar to lesson duration selector pattern
         * Note: htmlFor removed since input is nested inside label (implicit association)
         */
        if (layout === "pill") {
            return (
                <div className={finalWrapperClass}>
                    <label>
                        <input {...inputProps} />
                        {label && <span className="custom-input-pill-label">{label}</span>}
                    </label>

                    {/* Error message (displayed below pill) */}
                    {error && (
                        <p id={`${inputId}-error`} className="custom-input-error">
                            {error}
                        </p>
                    )}
                </div>
            );
        }

        // Fallback (shouldn't reach here)
        return null;
    }
);

CustomFormInput.displayName = "CustomFormInput";

CustomFormInput.propTypes = {
    /** Input type: 'checkbox' or 'radio' */
    type: PropTypes.oneOf(["checkbox", "radio"]),

    /** Color variant matching Dance Localé brand */
    variant: PropTypes.oneOf(INPUT_VARIANTS),

    /** Visual layout of the input: 'standard' (traditional) or 'pill' (button-like) */
    layout: PropTypes.oneOf(INPUT_STYLES),

    /** Label content displayed next to/inside the input (string or JSX node) */
    label: PropTypes.node,

    /** Additional CSS classes */
    className: PropTypes.string,

    /** Disabled state */
    disabled: PropTypes.bool,

    /** Error message (if validation fails) */
    error: PropTypes.string,

    /** Hint text displayed below input (not shown if error exists) */
    hint: PropTypes.string,

    /** Unique ID for the input (auto-generated if not provided) */
    id: PropTypes.string,

    /** Input name (required for radio groups) */
    name: PropTypes.string,

    /** Input value (required for radio buttons) */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),

    /** Checked state (for controlled components) */
    checked: PropTypes.bool,

    /** Change handler (for controlled components) */
    onChange: PropTypes.func,
};

export default CustomFormInput;
